import React, {
    createContext,
    ReactElement,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { PlayerStartStage } from '@yandex-int/ims.stream-player-public-types/build-es5/PlayerStartStage';
import { VideoPlayingState } from '@yandex-int/ims.stream-player-public-types/build-es5/VideoPlayingState';
import { PlayerListSdkItem } from '@yandex-int/ims.stream-player-public-types/build-es5/list-sdk/PlayerListSdkItem';
import { OnProgessChange, OobservCb, PlayerApi, VideoPlayerState } from '../player/types';

interface VideoPlayerSlot extends Omit<PlayerListSdkItem, 'container' | 'onMute' | 'onUnMute'> {
    container: HTMLElement;
    onToggleMuteUnmute?: (mute: boolean) => void;
    onVideoEnd?: VoidFunction;
    onVideoStart?: VoidFunction;
    onVideoPlay?: VoidFunction;
    onVideoPause?: VoidFunction;
}

interface VideoPlayerContextPayload {
    playerState: VideoPlayerState;
    setActiveItem: (node: HTMLElement) => void;
    pause: (node: HTMLElement) => void;
    play: (node: HTMLElement) => void;
    setMuted: (muted: boolean) => void;
    getActiveNode: () => HTMLElement | null;
    mount: (slot: VideoPlayerSlot) => (() => void) | undefined;
    isPlayerEnabled: boolean;
    onProgessChange: OnProgessChange;
    onIntersection: (node: HTMLElement, callback: OobservCb) => VoidFunction;
}

export const VideoPlayerContext = createContext<VideoPlayerContextPayload | null>(null);

export const useVideoPlayer = (): VideoPlayerContextPayload | null => {
    const playerContext = useContext(VideoPlayerContext);

    return playerContext;
};

interface VideoPlayerProviderPayload {
    children: ReactElement;
}

const defaultPlayerState: VideoPlayerState = {
    playingState: VideoPlayingState.END,
    startStage: PlayerStartStage.init,
    muted: false,
    isShortVideo: false,
};

export const VideoPlayerProvider = (props: VideoPlayerProviderPayload) => {
    const { children } = props;
    const [playerState, setPlayerState] = useState<Readonly<VideoPlayerState>>(defaultPlayerState);
    const [isPlayerEnabled, setPlayerEnabled] = useState(false);
    const [player, setPlayer] = useState<PlayerApi | null>();

    useEffect(() => {
        // Player содержит в себе пакет, который нельзя использовать на сервере,
        // поэтому импортим его в useEffect, что гарантирует нам вызов на клиенте
        import('../player/Player').then(({ default: Player }) => {
            setPlayer(new Player({ setPlayerState, setPlayerEnabled }));
        });
    }, []);

    const videoPlayerContext = useMemo(() => {
        if (!player) {
            return null;
        }

        return {
            ...player,
            getActiveNode: () => player.getActiveSlot()?.container || null,
            playerState,
            isPlayerEnabled,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [player, playerState]);

    return (
        <VideoPlayerContext.Provider value={videoPlayerContext}>
            {children}
        </VideoPlayerContext.Provider>
    );
};
